import { Mail, Phone, MapPin, Clock, Send, ChevronRight, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import Header from "@/components/Header"
import Footer from "@/components/Footer"
import { useLanguage } from "@/hooks/useLanguage"
import { SEO } from "@/components/SEO"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { contactSchema, ContactFormData } from "@/lib/validationSchemas"
import { apiClient } from "@/api/client"
import { toast } from "sonner"
import Turnstile, { type BoundTurnstileObject } from "react-turnstile"
import { useRef, useState } from "react"

const Contact = () => {
  const { t } = useLanguage()
  const text = t.contact
  const [isSubmitting, setIsSubmitting] = useState(false)
  const turnstileRef = useRef<BoundTurnstileObject | null>(null)

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
      honeypot: "",
      turnstileToken: "",
    },
  })

  const resetTurnstile = () => {
    setValue("turnstileToken", "")
    turnstileRef.current?.reset()
  }

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true)
    try {
      await apiClient.sendContactInquiry(data)
      toast.success(text.successMessage)
      reset()
    } catch (error: any) {
      if (error.status === 429) {
        toast.error("Too many requests. Please try again later.")
      } else {
        toast.error("Failed to send message. Please try again.")
        console.error("Contact error:", error)
      }
    } finally {
      resetTurnstile()
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO title={t.seo.contactTitle} description={t.seo.contactDesc} />
      <Header />

      <main>
        {/* Modern Industrial Hero */}
        <section className="relative border-b-2 border-foreground/5 pb-32 pt-24">
          <div className="container mx-auto px-4">
            <div className="flex flex-col items-start justify-between gap-12 md:flex-row md:items-end">
              <div className="max-w-3xl space-y-6">
                <span className="text-xs font-bold uppercase tracking-[0.4em] text-primary">
                  {text.subtitle}
                </span>
                <h1 className="font-display text-7xl uppercase leading-none tracking-tighter text-foreground md:text-9xl">
                  {text.title}
                </h1>
              </div>
              <div className="hidden pb-4 lg:block">
                <div className="mx-auto h-32 w-px bg-foreground/20" />
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section - Layered Industrial */}
        <section className="bg-muted/30 py-32">
          <div className="container mx-auto px-4">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-px overflow-hidden border-[1px] border-foreground/10 bg-foreground/10 shadow-2xl lg:grid-cols-12">
              {/* Contact Form Area */}
              <div className="space-y-12 bg-card p-12 backdrop-blur-sm md:p-20 lg:col-span-7">
                <div className="space-y-4">
                  <h2 className="font-display text-5xl uppercase tracking-widest text-foreground">
                    {text.getInTouch}
                  </h2>
                  <div className="h-[1px] w-12 bg-primary" />
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                  {/* Honeypot field - hidden from users but visible to bots */}
                  <div className="hidden">
                    <Input
                      id="honeypot"
                      {...register("honeypot")}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label
                        htmlFor="name"
                        className="text-xs font-black uppercase tracking-[0.2em] text-primary"
                      >
                        {text.name}
                      </Label>
                      <Input
                        id="name"
                        {...register("name")}
                        className={`h-14 rounded-none border-foreground/20 bg-background focus:border-primary ${errors.name ? "border-destructive" : ""}`}
                        placeholder="ALBERTO MÁRQUEZ"
                      />
                      {errors.name && (
                        <p className="text-xs font-bold text-destructive uppercase tracking-tighter">
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="email"
                        className="text-xs font-black uppercase tracking-[0.2em] text-primary"
                      >
                        {text.email}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        {...register("email")}
                        className={`h-14 rounded-none border-foreground/20 bg-background focus:border-primary ${errors.email ? "border-destructive" : ""}`}
                        placeholder="ALBERTO@MEATBID.MX"
                      />
                      {errors.email && (
                        <p className="text-xs font-bold text-destructive uppercase tracking-tighter">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="subject"
                      className="text-xs font-black uppercase tracking-[0.2em] text-primary"
                    >
                      {text.subject}
                    </Label>
                    <Controller
                      name="subject"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <SelectTrigger className={`h-14 rounded-none border-foreground/20 bg-background font-display text-xl tracking-widest focus:ring-primary ${errors.subject ? "border-destructive" : ""}`}>
                            <SelectValue placeholder={text.subject} />
                          </SelectTrigger>
                          <SelectContent className="rounded-none border-[1px] border-foreground/20 bg-card">
                            <SelectItem
                              value="general"
                              className="py-3 font-display text-lg tracking-wider"
                            >
                              {text.subjects.general}
                            </SelectItem>
                            <SelectItem
                              value="seller"
                              className="py-3 font-display text-lg tracking-wider"
                            >
                              {text.subjects.seller}
                            </SelectItem>
                            <SelectItem
                              value="buyer"
                              className="py-3 font-display text-lg tracking-wider"
                            >
                              {text.subjects.buyer}
                            </SelectItem>
                            <SelectItem
                              value="partnership"
                              className="py-3 font-display text-lg tracking-wider"
                            >
                              {text.subjects.partnership}
                            </SelectItem>
                            <SelectItem
                              value="support"
                              className="py-3 font-display text-lg tracking-wider"
                            >
                              {text.subjects.support}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.subject && (
                      <p className="text-xs font-bold text-destructive uppercase tracking-tighter">
                        {errors.subject.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="message"
                      className="text-xs font-black uppercase tracking-[0.2em] text-primary"
                    >
                      {text.message}
                    </Label>
                    <Textarea
                      id="message"
                      rows={8}
                      {...register("message")}
                      className={`rounded-none border-foreground/20 bg-background text-base focus:border-primary ${errors.message ? "border-destructive" : ""}`}
                      placeholder={text.messagePlaceholder}
                    />
                    {errors.message && (
                      <p className="text-xs font-bold text-destructive uppercase tracking-tighter">
                        {errors.message.message}
                      </p>
                    )}
                  </div>

                  {/* Cloudflare Turnstile */}
                  <div className="flex justify-start">
                    <Turnstile
                      sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"}
                      onVerify={(token, turnstile) => {
                        turnstileRef.current = turnstile
                        setValue("turnstileToken", token)
                      }}
                      onLoad={(_, turnstile) => {
                        turnstileRef.current = turnstile
                      }}
                      onExpire={resetTurnstile}
                      onError={resetTurnstile}
                      onTimeout={resetTurnstile}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex h-auto w-full items-center justify-center gap-4 rounded-none bg-action py-8 font-display text-3xl tracking-widest text-action-foreground shadow-2xl transition-all hover:scale-[1.01] hover:brightness-110 active:scale-95 disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t.contact.processing}
                      </>
                    ) : (
                      <>
                        {t.contact.send}
                        <Send className="h-6 w-6" />
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Information Sidebar */}
              <div className="flex flex-col justify-between space-y-16 bg-foreground p-12 text-background dark:bg-[#121212] dark:text-foreground md:p-20 lg:col-span-5">
                <div className="space-y-12">
                  <h2 className="font-display text-5xl uppercase tracking-widest text-background dark:text-foreground">
                    {text.contactInfo}
                  </h2>

                  <div className="space-y-10">
                    <div className="group space-y-2">
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-background/50 dark:text-foreground/50">
                        {text.address}
                      </p>
                      <p className="text-xl font-medium transition-opacity group-hover:opacity-70">
                        {text.addressText}
                      </p>
                    </div> 

                    <div className="group space-y-2">
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-background/50 dark:text-foreground/50">
                        {text.emailAddress}
                      </p>
                      <a
                        href={`mailto:${text.emailText}`}
                        className="font-display text-3xl tracking-widest transition-opacity hover:opacity-70"
                      >
                        {text.emailText}
                      </a>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-background/50 dark:text-foreground/50">
                        {text.hours}
                      </p>
                      <p className="text-xl font-medium">{text.hoursText}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default Contact

